"use client";

import React, { useState, useEffect, useRef } from "react";
import { Search, Plus, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { fetchUsers } from "@/api/fetchUsers/fetchUsersApi";
import { User } from "@/api/fetchUsers/types";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";

interface NewAttendeeFields {
  firstName: string;
  lastName: string;
  email?: string;
}

interface AddAttendeesProps {
  onAddAttendee: (attendee: NewAttendeeFields) => void;
  duplicateEmailError: string | null;
  onClearDuplicateEmailError: () => void;
}

export default function AddAttendees({
  onAddAttendee,
  duplicateEmailError,
  onClearDuplicateEmailError,
}: AddAttendeesProps) {
  // Employee search
  const [searchQuery, setSearchQuery] = useState("");
  const debouncedSearchQuery = useDebouncedValue(searchQuery, 500);
  const [isSearching, setIsSearching] = useState(false);
  const [searchResults, setSearchResults] = useState<User[]>([]);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const searchContainerRef = useRef<HTMLDivElement>(null);

  // Manual entry fields
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");

  useEffect(() => {
    setSearchError(null);
    if (debouncedSearchQuery.length < 2) {
      setSearchResults([]);
      return;
    }
    setIsSearching(true);
    async function fetchEmployees() {
      try {
        const response = await fetchUsers({
          searchTerm: debouncedSearchQuery,
          pageSize: 10,
        });
        setSearchResults(response.data.users || []);
      } catch (error) {
        console.error("Error searching for employees:", error);
        setSearchError("Failed to fetch employees. Please try again.");
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    }
    fetchEmployees();
  }, [debouncedSearchQuery]);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (
        searchContainerRef.current &&
        !searchContainerRef.current.contains(e.target as Node)
      ) {
        setIsSearchFocused(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  function handleSearchChange(e: React.ChangeEvent<HTMLInputElement>) {
    setSearchQuery(e.target.value);
    if (duplicateEmailError) onClearDuplicateEmailError();
  }

  function addEmployeeAttendee(emp: User) {
    onAddAttendee({
      firstName: emp.firstName,
      lastName: emp.lastName,
      email: emp.email,
    });
    setSearchQuery("");
    setSearchResults([]);
    setIsSearchFocused(false);
  }

  function addManualAttendee() {
    if (!firstName && !lastName) return;
    onAddAttendee({ firstName, lastName, email: email || undefined });
    if (!duplicateEmailError) {
      setFirstName("");
      setLastName("");
      setEmail("");
    }
  }

  const showDropdown =
    isSearchFocused &&
    (isSearching ||
      searchResults.length > 0 ||
      (debouncedSearchQuery.length >= 2 && searchResults.length === 0) ||
      searchError);

  return (
    <div className="space-y-3">
      {/* Employee search */}
      <div ref={searchContainerRef} className="relative z-20">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground/50" />
          <Input
            className="pl-9"
            onChange={handleSearchChange}
            onFocus={() => setIsSearchFocused(true)}
            placeholder="Search employees by name or email..."
            value={searchQuery}
          />
        </div>

        {showDropdown && (
          <div className="absolute z-30 w-full mt-1 bg-white dark:bg-slate-900 border border-border rounded-md shadow-md">
            {isSearching ? (
              <div className="flex justify-center py-3">
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
              </div>
            ) : searchError ? (
              <p className="text-sm text-destructive px-3 py-2">{searchError}</p>
            ) : searchResults.length > 0 ? (
              <div className="divide-y divide-border max-h-48 overflow-y-auto">
                {searchResults.map((emp) => (
                  <button
                    className="flex items-center justify-between w-full px-3 py-2 text-left hover:bg-muted/50 transition-colors"
                    key={emp.id}
                    onClick={() => addEmployeeAttendee(emp)}
                    type="button"
                  >
                    <div className="min-w-0">
                      <span className="text-sm font-medium">
                        {emp.firstName} {emp.lastName}
                      </span>
                      <span className="text-xs text-muted-foreground ml-2">
                        {emp.email}
                      </span>
                    </div>
                    <Plus className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0 ml-2" />
                  </button>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground px-3 py-2">
                No employees found.
              </p>
            )}
          </div>
        )}
      </div>

      {/* Manual entry */}
      <div className="relative z-0">
        {duplicateEmailError && (
          <p className="text-sm text-destructive mb-2">{duplicateEmailError}</p>
        )}
        <div className="flex flex-col sm:flex-row gap-2">
          <Input
            className="sm:flex-1"
            onChange={(e) => setFirstName(e.target.value)}
            placeholder="First name"
            value={firstName}
          />
          <Input
            className="sm:flex-1"
            onChange={(e) => setLastName(e.target.value)}
            placeholder="Last name"
            value={lastName}
          />
          <Input
            className="sm:flex-1"
            onChange={(e) => {
              setEmail(e.target.value);
              if (duplicateEmailError) onClearDuplicateEmailError();
            }}
            placeholder="Email"
            type="email"
            value={email}
          />
          <Button
            className="px-4 flex-shrink-0"
            disabled={!firstName && !lastName}
            onClick={addManualAttendee}
            type="button"
            variant="outline"
          >
            <Plus className="h-3.5 w-3.5 mr-1.5" />
            Add
          </Button>
        </div>
      </div>
    </div>
  );
}
